import { Data } from "rete/types/core/data"
import { BooleanTileGrid, CategoricalTileGrid, NumericTileGrid } from "../modelling/tile_grid"
import type { tileGridStats } from "../modelling/tile_grid"
import {
  categoryGroupKeyForLabel,
  getTableDetailEntry,
  getTableDetailEntries,
  type TableDetailEntry,
} from "../editor/model_detail"
import { labelEntriesFromGrid } from "./categorical_layer_colors"
import type { DatasetLayer, Layer, ModelOutputLayer } from "../state"

export const CATEGORICAL_VIEW_CLASSES = "classes"

export type CategoricalTableRowViewOption = {
  value: string
  label: string
  tableNodeId: number
  rowIndex: number
}

export type CategoricalTableRowMapping = {
  valuesByCategoryKey: Map<number, number>
  min: number
  max: number
}

export function parseTableRowView(
  value: string | undefined
): { tableNodeId: number; rowIndex: number } | null {
  if (!value || value === CATEGORICAL_VIEW_CLASSES) return null
  const match = /^tableRow:(\d+):(\d+)$/.exec(value)
  if (!match) return null
  return { tableNodeId: Number(match[1]), rowIndex: Number(match[2]) }
}

export function isCategoricalTableRowView(layer: Pick<ModelOutputLayer | DatasetLayer, "categoricalView">): boolean {
  return parseTableRowView(layer.categoricalView) !== null
}

/** True when one normalized key list is a subset of the other (order ignored). */
function categoricalKeySetsCompatible(keysA: string[], keysB: string[]): boolean {
  if (keysA.length === 0 || keysB.length === 0) return false
  return keysA.every(key => keysB.includes(key)) || keysB.every(key => keysA.includes(key))
}

/**
 * True when the table columns and grid labels are compatible for linking:
 * one set contains the other (exact match, or one has extra categories).
 * Unmatched categories are skipped when computing row means / numeric views.
 */
export function tableColumnsMatchCategoricalGrid(
  table: TableDetailEntry,
  grid: CategoricalTileGrid
): boolean {
  const gridKeys = labelEntriesFromGrid(grid).map(entry => categoryGroupKeyForLabel(entry.label))
  const tableKeys = table.columns.map(column => categoryGroupKeyForLabel(column))
  return categoricalKeySetsCompatible(gridKeys, tableKeys)
}

export function categoricalLabelKeysForGrid(grid: CategoricalTileGrid): string[] {
  return labelEntriesFromGrid(grid)
    .map(entry => categoryGroupKeyForLabel(entry.label))
    .sort()
}

/**
 * True when two categorical grids can be compared: same class labels, or one
 * is a subset of the other (e.g. an extra category on one side).
 */
export function categoricalLabelKeysMatch(
  gridA: CategoricalTileGrid,
  gridB: CategoricalTileGrid
): boolean {
  return categoricalKeySetsCompatible(
    categoricalLabelKeysForGrid(gridA),
    categoricalLabelKeysForGrid(gridB)
  )
}

export type ComparableCategoricalLayer = {
  layerId: number
  name: string
  layer: ModelOutputLayer | DatasetLayer
}

export function findComparableCategoricalLayers(
  projectLayers: Record<number, Layer>,
  layerOrder: number[],
  currentLayerId: number | undefined,
  currentGrid: CategoricalTileGrid,
  layerStats: (
    layer: ModelOutputLayer | DatasetLayer
  ) => BooleanTileGrid | NumericTileGrid | CategoricalTileGrid | null
): ComparableCategoricalLayer[] {
  if (currentLayerId === undefined) return []

  const comparable: ComparableCategoricalLayer[] = []
  for (const layerId of layerOrder) {
    if (layerId === currentLayerId) continue
    const layer = projectLayers[layerId]
    if (!layer || (layer.type !== "ModelOutputLayer" && layer.type !== "DatasetLayer")) continue

    const grid = layerStats(layer)
    if (!(grid instanceof CategoricalTileGrid)) continue
    if (!categoricalLabelKeysMatch(currentGrid, grid)) continue

    comparable.push({ layerId, name: layer.name, layer })
  }
  return comparable
}

export function tableRowMeansForTableNode(
  chartData: { tableRowMeansTables?: TableRowMeansTable[]; tableRowMeans?: TableRowMeanEntry[] },
  tableNodeId: number
): TableRowMeanEntry[] {
  const table = chartData.tableRowMeansTables?.find(entry => entry.nodeId === tableNodeId)
  if (table) return table.rows
  if (tableNodeId === -1 || chartData.tableRowMeansTables?.length === 0) {
    return chartData.tableRowMeans ?? []
  }
  return []
}

/** Row-wise difference: primary mean minus compare mean (matched by row label). */
export function computeTableRowMeanDifference(
  primaryRows: TableRowMeanEntry[],
  compareRows: TableRowMeanEntry[]
): TableRowMeanEntry[] {
  const compareByLabel = new Map(
    compareRows.map(row => [row.label.trim().toLowerCase(), row.mean])
  )

  return primaryRows.flatMap(row => {
    const compareMean = compareByLabel.get(row.label.trim().toLowerCase())
    if (compareMean === undefined) return []
    return [{ label: row.label, mean: row.mean - compareMean }]
  })
}

/** Exposed tables whose columns are compatible with the grid's class labels. */
export function getLinkedTablesForLayer(
  model: Data | null,
  _layer: ModelOutputLayer | DatasetLayer,
  grid: CategoricalTileGrid
): TableDetailEntry[] {
  if (!model?.nodes) return []

  return getTableDetailEntries(model).filter(table =>
    table.rows.length > 0 && tableColumnsMatchCategoricalGrid(table, grid)
  )
}

export function getPrimaryLinkedTableForLayer(
  model: Data | null,
  layer: ModelOutputLayer | DatasetLayer,
  grid: CategoricalTileGrid
): TableDetailEntry | null {
  const tables = getLinkedTablesForLayer(model, layer, grid)
  return tables[0] ?? null
}

export type TableRowMeanEntry = {
  label: string
  mean: number
}

export type TableRowMeansTable = {
  nodeId: number
  name: string
  rows: TableRowMeanEntry[]
}

/** Area-weighted mean of each table row from category area counts in the snapshot extent. */
export function computeTableRowMeansFromCategoryCounts(
  categoryCounts: Map<unknown, number>,
  table: TableDetailEntry
): TableRowMeanEntry[] {
  const rows: TableRowMeanEntry[] = []

  for (let rowIndex = 0; rowIndex < table.rows.length; rowIndex++) {
    let weightedSum = 0
    let totalWeight = 0

    for (const [label, area] of Array.from(categoryCounts.entries())) {
      if (typeof label !== "string" || label.trim().toLowerCase() === "no data") continue
      if (typeof area !== "number" || !Number.isFinite(area) || area <= 0) continue

      const columnIndex = table.columns.findIndex(
        column => categoryGroupKeyForLabel(column) === categoryGroupKeyForLabel(label)
      )
      if (columnIndex < 0) continue

      const cellValue = table.cells[rowIndex]?.[columnIndex]
      if (typeof cellValue !== "number" || !Number.isFinite(cellValue)) continue

      weightedSum += cellValue * area
      totalWeight += area
    }

    if (totalWeight <= 0) continue

    const rowLabel = table.rows[rowIndex]?.trim() || `Row ${rowIndex + 1}`
    rows.push({ label: rowLabel, mean: weightedSum / totalWeight })
  }

  return rows
}

export function tablesFingerprintForCache(tables: TableDetailEntry[]): string | null {
  if (tables.length === 0) return null
  return JSON.stringify(tables.map(table => tableFingerprintForCache(table)))
}

export function tableFingerprintForCache(table: TableDetailEntry | null | undefined): string | null {
  if (!table) return null
  return JSON.stringify({
    nodeId: table.nodeId,
    rows: table.rows,
    columns: table.columns,
    cells: table.cells,
  })
}

export function getCategoricalTableRowViewOptionsForTable(
  table: TableDetailEntry
): CategoricalTableRowViewOption[] {
  return table.rows.map((rowLabel, rowIndex) => {
    const trimmed = rowLabel.trim()
    const rowName = trimmed || `Row ${rowIndex + 1}`
    return {
      value: `tableRow:${table.nodeId}:${rowIndex}`,
      label: rowName,
      tableNodeId: table.nodeId,
      rowIndex,
    }
  })
}

export function getCategoricalTableRowViewOptions(
  model: Data | null,
  layer: ModelOutputLayer | DatasetLayer,
  grid: CategoricalTileGrid | null | undefined
): CategoricalTableRowViewOption[] {
  if (!grid) return []

  const tables = getLinkedTablesForLayer(model, layer, grid)
  if (tables.length === 0) return []

  const multipleTables = tables.length > 1
  const options: CategoricalTableRowViewOption[] = []

  for (const table of tables) {
    for (const option of getCategoricalTableRowViewOptionsForTable(table)) {
      options.push(
        multipleTables
          ? { ...option, label: `${table.name} — ${option.label}` }
          : option
      )
    }
  }

  return options
}

export function resolveCategoricalTableRowMapping(
  grid: CategoricalTileGrid,
  table: TableDetailEntry,
  rowIndex: number
): CategoricalTableRowMapping | null {
  const valuesByCategoryKey = new Map<number, number>()
  let min = Infinity
  let max = -Infinity

  for (const { key, label } of labelEntriesFromGrid(grid)) {
    const columnIndex = table.columns.findIndex(
      column => categoryGroupKeyForLabel(column) === categoryGroupKeyForLabel(label)
    )
    if (columnIndex < 0) continue

    const cellValue = table.cells[rowIndex]?.[columnIndex]
    if (typeof cellValue !== "number" || !Number.isFinite(cellValue)) continue

    valuesByCategoryKey.set(key, cellValue)
    min = Math.min(min, cellValue)
    max = Math.max(max, cellValue)
  }

  if (valuesByCategoryKey.size === 0) return null
  if (!Number.isFinite(min)) min = 0
  if (!Number.isFinite(max)) max = min === 0 ? 1 : min

  return { valuesByCategoryKey, min, max }
}

export function resolveTableRowMappingForLayer(
  model: Data | null,
  layer: ModelOutputLayer | DatasetLayer,
  grid: CategoricalTileGrid
): CategoricalTableRowMapping | null {
  const parsed = parseTableRowView(layer.categoricalView)
  if (!parsed || !model) return null

  const table = getTableDetailEntry(model, parsed.tableNodeId)
  if (!table) return null
  if (!tableColumnsMatchCategoricalGrid(table, grid)) return null
  if (parsed.rowIndex < 0 || parsed.rowIndex >= table.rows.length) return null

  return resolveCategoricalTableRowMapping(grid, table, parsed.rowIndex)
}

export function categoricalViewCacheKey(layer: Pick<ModelOutputLayer | DatasetLayer, "categoricalView">): string {
  return layer.categoricalView ?? CATEGORICAL_VIEW_CLASSES
}

export function numericBoundsForTableRowView(
  layer: ModelOutputLayer | DatasetLayer,
  mapping: CategoricalTableRowMapping
): [number, number] {
  if (layer.overrideBounds && layer.bounds) {
    return layer.bounds
  }
  if (mapping.min === mapping.max) {
    return [mapping.min, mapping.max + 1]
  }
  return [mapping.min, mapping.max]
}

export function effectiveLegendStatsForLayer(
  layer: ModelOutputLayer | DatasetLayer,
  grid: BooleanTileGrid | NumericTileGrid | CategoricalTileGrid | null | undefined,
  stats: tileGridStats,
  model: Data | null
): tileGridStats {
  if (!(grid instanceof CategoricalTileGrid) || stats.type !== "CategoricalTileGrid") {
    return stats
  }

  const mapping = resolveTableRowMappingForLayer(model, layer, grid)
  if (!mapping) return stats

  const [min, max] = numericBoundsForTableRowView(layer, mapping)
  return {
    ...stats,
    type: "NumericTileGrid",
    min,
    max,
  }
}

export function numericDisplayValueForCategoricalCell(
  layer: ModelOutputLayer | DatasetLayer,
  grid: CategoricalTileGrid,
  categoryValue: number,
  model: Data | null
): string | null {
  if (categoryValue === 255) return null
  const mapping = resolveTableRowMappingForLayer(model, layer, grid)
  if (!mapping) return null

  const numericValue = mapping.valuesByCategoryKey.get(categoryValue)
  if (numericValue === undefined || !Number.isFinite(numericValue)) return null

  return Number.isInteger(numericValue) ? String(numericValue) : numericValue.toPrecision(4)
}

/** Stable cache fingerprint for a table-row numeric view applied to a categorical grid. */
export function categoricalNumericViewCacheKey(
  layer: ModelOutputLayer | DatasetLayer,
  mapping: CategoricalTableRowMapping
): string {
  const entries = Array.from(mapping.valuesByCategoryKey.entries()).sort((a, b) => a[0] - b[0])
  const bounds = numericBoundsForTableRowView(layer, mapping)
  return JSON.stringify({ view: layer.categoricalView, entries, bounds })
}
